// Path: ./src/app/pages/StoreUploadedImagePage/index.tsx
import { Box, Stack } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { adminApiService } from "../../../utils/api";
import { GfStore, MuiFilterRequestDto } from "@swagger/typescript-fetch-goflyer";
import { GridFilterModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

import { messages } from "../../../locales/messages";
import { useNavigate } from "react-router-dom";
import { TableData } from "../../../utils/interface/tableData";
import { useStoreUploadedImageTableColumns } from "./useStoreUploadedImageTableColumns";
import { SetupPageTopAppBar } from "../../../utils/commonStyledComponents/commonStyled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import { LeftButtonSection, TableWrapper } from "./styled";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { SUPERADMIN } from "../../../utils/constant/stringConstant";
import { routes } from "../../../utils/constant/navigationRoutes";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";

const StoreUploadedImagePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useContext(GoFlyerAdminContext);
  const authHeaders = useAuthHeaders();
  const isSuperAdmin = state.user?.parsedRoles?.name === SUPERADMIN;

  const { paginationModel, sortModel, handleTableChange } = useQueryParams();

  const [paginationLoading, setPaginationLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [flyerListData, setFlyerListData] = useState<TableData<GfStore>>();
  const flyerTableColumns = useStoreUploadedImageTableColumns();

  const fetchFlyerItemList = async () => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        if (!isSuperAdmin) {
          return {
            data: state.user?.stores,
            total: state.user?.stores?.length,
          };
        }
        return adminApiService.gfFlyerItemImageAdminControllerGetStoresWithFlyerItemImage(
          paginationModel?.page,
          paginationModel?.pageSize,
        );
      },
      (flyerItemList) => {
        setFlyerListData((prev) => ({
          ...prev,
          rows: flyerItemList?.data,
          total: flyerItemList?.total,
        }));
      },
    );
  };

  const fetchFlyerItemListWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfStoreAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setFlyerListData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  useEffect(() => {
    setFlyerListData({
      columns: flyerTableColumns,
    });
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) {
      fetchFlyerItemList();
      return;
    }
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchFlyerItemListWithFilter(filterModel);
    } else {
      fetchFlyerItemList();
    }
  }, [paginationModel, sortModel, filterModel, isSuperAdmin]);

  return (
    <Box data-testid="storeuploadedimagepage-test-id">
      <SetupPageTopAppBar>
        <LeftButtonSection>
          <Stack direction="row" spacing={2} alignItems={"center"}>
            <BlackFontTransparentButton
              variant="text"
              onClick={() => {
                let StoreId = "2ff26dcc-8e97-4806-a5b7-82c6884dff11";
                if (!isSuperAdmin) {
                  if (state.user?.stores?.length) {
                    StoreId = state.user?.stores[0].id;
                  }
                }
                navigate(routes.camera.byStoreId(StoreId));
              }}
              startIcon={
                <UploadFileIcon style={{ color: "#ff8038", fontSize: 25 }} />
              }
            >
              {t(messages.Create())}
            </BlackFontTransparentButton>
          </Stack>
        </LeftButtonSection>
      </SetupPageTopAppBar>
      <TableWrapper>
        <ListTableQueryPagination
          loadFailed={listRequestGuard.loadFailed}
          sortingMode="client"
          columns={flyerListData?.columns}
          rows={flyerListData?.rows}
          total={paginationLoading ? 0 : flyerListData?.total}
          paginationModel={paginationModel}
          sortModel={sortModel}
          isLoading={paginationLoading}
          onTableChange={handleTableChange}
          onRowClick={(row) => {
            navigate(routes.storeUploadImage.byStoreId(String(row?.id)));
          }}
          dynamicHeightAdjuster={210}
          filterModel={filterModel}
          onFilterModelChange={
            isSuperAdmin ? handleFilterModelChange : undefined
          }
        />
      </TableWrapper>
    </Box>
  );
};

export default StoreUploadedImagePage;
